import React, { useEffect, useState } from "react";
import Modal from "./Modal.tsx";
import { useForm } from "react-hook-form";
import SelectField from "../Forms/SelectField.js";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { getMedications } from "../../services/medication.js";
import toast from "react-hot-toast";
import TextAreaField from "../Forms/TextAreaField.js";
import { createPRN } from "../../services/users.js";
import { useParams } from "react-router-dom";
import {
  adminTypeOptions,
  dosageOptions,
  formOptions,
  quantityOptions,
  routeOptions,
} from "../../utils/helper.js";
import InputField from "../Forms/InputField.js";

const validationSchema = Yup.object().shape({
  name: Yup.string().required("Medication is required"),
  dose: Yup.string().required("Dosage is required"),
  customDose: Yup.string().when("dose", {
    is: "Other",
    then: (schema) => schema.required("Custom dosage is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  quantity: Yup.string().required("Quantity is required"),
  route: Yup.string().required("Route is required"),
  form: Yup.string().required("Form is required"),
  adminType: Yup.string().required("Admin type is required"),
  instructions: Yup.string().required("Instructions are required"),
});
export const AddPRN = ({ isOpen, onClose, reload, setPrn, prn }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [medicationOptions, setMedicationOptions] = useState([]);
  const urlParam = useParams();

  const {
    handleSubmit,
    control,
    formState: { errors },
    watch,
  } = useForm({
    resolver: yupResolver(validationSchema),
  });

  const dose = watch("dose");

  const onSubmit = async (data) => {
    setIsLoading(true);
    const finalData = {
      ...data,
      dose: data.dose === "Other" ? data.customDose : data.dose,
    };
    delete finalData.customDose;

    const payload = {
      ...finalData,
      serviceUserId: urlParam.id,
    };
    try {
      const response = await createPRN(urlParam.id, payload);
      if (response.status === 200) {
        reload();
        onClose(false);
        toast.success("PRN medication created successfully");
      }
    } catch (error) {
      console.log(error);
      toast.error("Failed to create medication");
    }
    setIsLoading(false);
  };

  const fetchMedication = async () => {
    setIsLoading(true);
    try {
      const { data } = await getMedications();
      const options = data.map((medication) => ({
        label: medication.name,
        value: medication.name,
      }));
      setMedicationOptions(options);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchMedication();
  }, []);
  return (
    <Modal
      title={`Add PRN Medication`}
      size="md"
      onCancel={() => onClose(false)}
      isOpen={isOpen}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <SelectField
          control={control}
          name="name"
          label="Medication"
          error={errors.name?.message}
          options={medicationOptions}
        />
        <SelectField
          control={control}
          name="dose"
          label="Dosage"
          error={errors.dose?.message}
          options={dosageOptions}
        />
        {dose === "Other" && (
          <InputField
            control={control}
            name="customDose"
            label="Custom Dosage"
            error={errors.customDose?.message}
            placeholder="Enter custom dosage (e.g., 25mg, 0.5ml)"
            required
          />
        )}
        <SelectField
          control={control}
          name="quantity"
          label="Quantity"
          options={quantityOptions}
          error={errors.quantity?.message}
        />
        <SelectField
          control={control}
          name="route"
          label="Route"
          error={errors.route?.message}
          options={routeOptions}
        />
        <SelectField
          control={control}
          name="form"
          label="Form"
          error={errors.form?.message}
          options={formOptions}
        />
        <SelectField
          control={control}
          name="adminType"
          label="Admin type"
          error={errors.adminType?.message}
          options={adminTypeOptions}
        />
        <TextAreaField
          name="instructions"
          control={control}
          label="Instructions"
          error={errors.instructions?.message}
        />

        <button
          type="submit"
          className="btn btn__primary btn--block"
          disabled={isLoading}
        >
          {isLoading ? "Loading" : "Submit"}
        </button>
      </form>
    </Modal>
  );
};
